/**
 * Spec 041: the pure view model for live paper sessions. Every function reads
 * nested objects with optional chaining and returns `—`/`FLAT` for anything
 * missing; none throws, none prints `undefined` or `NaN`. Time is always
 * passed in (`now` is epoch milliseconds), never read here.
 */
import {
  ACTIVITY_COPY,
  ACTIVITY_NONE,
  CLOSE_BASIS,
  ENDED_HEADLINES,
  POSITION_SENTENCES,
  STALLED_SENTENCE,
  STATE_PROBLEM_COPY,
  STOPPING_LABEL,
  STOP_REASONS,
  WAITING_CLOSED,
  WAITING_DOWN,
  endedFallbackHeadline,
  stateProblemOther,
} from './liveCopy'
import type { FeedState, FeedSymbol, LiveEquityPoint, LivePosition, LiveSession, LiveSessionsEnvelope } from './types'

const DASH = '—'
export const LIVE_ENDED = ['stopped', 'interrupted', 'failed'] as const
/** Price tick per instrument; an unknown instrument rounds to 2 decimals. */
const TICKS: Record<string, number> = { NQ: 0.25 }

const finite = (n: number | null | undefined): n is number => typeof n === 'number' && Number.isFinite(n)
const pad = (n: number): string => String(n).padStart(2, '0')

function group(n: number, digits: number): string {
  return n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

// ---- Formatting -----------------------------------------------------------

export function formatMoney(n: number | null | undefined): string {
  return finite(n) ? group(n, 2) : DASH
}

export function formatPnl(n: number | null | undefined): string {
  if (!finite(n)) return DASH
  const rounded = Math.round(n * 100) / 100
  if (rounded === 0) return '0.00'
  return rounded > 0 ? `+${group(rounded, 2)}` : `-${group(Math.abs(rounded), 2)}`
}

export function formatQty(n: number | null | undefined): string {
  return finite(n) ? String(Number(n.toFixed(6))) : DASH
}

export function formatPrice(n: number | null | undefined, instrument: string | null | undefined): string {
  if (!finite(n)) return DASH
  const tick = instrument === null || instrument === undefined ? undefined : TICKS[instrument]
  const snapped = tick === undefined ? n : Math.round(n / tick) * tick
  return group(snapped, 2)
}

export function positionText(position: LivePosition | null | undefined, instrument?: string | null): string {
  const side = position?.side
  const qty = position?.quantity
  if (typeof side !== 'string' || side === '' || !finite(qty) || qty === 0) return 'FLAT'
  const at = finite(position?.avg_price) ? ` @ ${formatPrice(position?.avg_price, instrument)}` : ''
  return `${side.toUpperCase()} ${formatQty(qty)}${at}`
}

export function localClock(iso: string | null | undefined): string {
  if (typeof iso !== 'string') return DASH
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return DASH
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

function parseMs(iso: string | null | undefined): number | null {
  if (typeof iso !== 'string') return null
  const ms = new Date(iso).getTime()
  return Number.isNaN(ms) ? null : ms
}

export function formatDuration(totalSeconds: number): string {
  const s = Math.max(0, Math.floor(totalSeconds))
  const days = Math.floor(s / 86400)
  const clock = `${pad(Math.floor((s % 86400) / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`
  return days > 0 ? `${days}d ${clock}` : clock
}

// ---- Classification -------------------------------------------------------

export function isEnded(session: LiveSession): boolean {
  if ((LIVE_ENDED as readonly string[]).includes(session.status)) return true
  const known = ['starting', 'running', 'stalled', 'stopping'].includes(session.status)
  return !known && typeof session.ended_at === 'string'
}

export function isActive(session: LiveSession): boolean {
  return !isEnded(session)
}

export function elapsedText(session: LiveSession, now: number): string {
  const start = parseMs(session.started_at) ?? parseMs(session.created_at)
  if (start === null) return DASH
  const end = isEnded(session) ? parseMs(session.ended_at) : now
  if (end === null) return DASH
  return formatDuration((end - start) / 1000)
}

export function paperPnl(session: LiveSession): number | null {
  const equity = isEnded(session) && session.final !== null && session.final !== undefined
    ? session.final.equity
    : session.state?.account?.equity
  const start = session.account?.starting_balance
  if (!finite(equity) || !finite(start)) return null
  return equity - start
}

function createdMs(session: LiveSession): number | null {
  return parseMs(session.created_at)
}

export function splitSessions(
  sessions: LiveSession[],
  slug: string,
): { running: LiveSession[]; ended: LiveSession[] } {
  const mine = sessions.filter((s) => s.strategy_slug === slug)
  const newestFirst = (a: LiveSession, b: LiveSession): number => {
    const x = createdMs(a)
    const y = createdMs(b)
    if (x === null && y === null) return 0
    if (x === null) return 1
    if (y === null) return -1
    return y - x
  }
  return {
    running: mine.filter(isActive).sort(newestFirst),
    ended: mine.filter(isEnded).sort(newestFirst),
  }
}

// ---- Activity, state problems, state age ----------------------------------

export function activityLine(
  session: LiveSession,
  shellFeedState: FeedState | null,
): { text: string; tone: 'neutral' | 'warn' } {
  if (session.status === 'stalled') return { text: STALLED_SENTENCE, tone: 'warn' }
  const activity = session.state?.activity
  if (typeof activity !== 'string' || activity === '') return { text: ACTIVITY_NONE, tone: 'neutral' }
  const base = ACTIVITY_COPY[activity] ?? activity
  if (activity !== 'waiting_for_feed') return { text: base, tone: 'neutral' }
  if (session.state?.feed?.state === 'window' || shellFeedState === 'closed') {
    return { text: `${base} ${WAITING_CLOSED}`, tone: 'neutral' }
  }
  if (shellFeedState === 'down' || shellFeedState === 'error') {
    return { text: `${base} ${WAITING_DOWN}`, tone: 'neutral' }
  }
  return { text: base, tone: 'neutral' }
}

export function stateProblemText(session: LiveSession, now: number): string | null {
  if (session.state !== null && session.state !== undefined) return null
  const problem = session.state_problem
  if (problem === null || problem === undefined || problem === '') return STATE_PROBLEM_COPY.none
  if (problem === 'not_written') {
    const start = parseMs(session.started_at) ?? parseMs(session.created_at)
    if (session.status === 'running' && start !== null && now - start > 60_000) return STATE_PROBLEM_COPY.not_written_old
    return STATE_PROBLEM_COPY.not_written
  }
  const known: Record<string, string> = {
    missing: STATE_PROBLEM_COPY.missing,
    unreadable: STATE_PROBLEM_COPY.unreadable,
    too_large: STATE_PROBLEM_COPY.too_large,
    malformed: STATE_PROBLEM_COPY.malformed,
    unsupported_version: STATE_PROBLEM_COPY.unsupported_version,
  }
  return known[problem] ?? stateProblemOther(problem)
}

export function stateAge(session: LiveSession): { stale: boolean; text: string | null } {
  const age = session.state_age_s
  if (session.status !== 'running' || !finite(age) || age <= 30) return { stale: false, text: null }
  return {
    stale: true,
    text: `Figures are old: last update ${localClock(session.state_as_of)} (${Math.round(age)} s ago).`,
  }
}

// ---- Stop control ---------------------------------------------------------

export interface StopControl {
  kind: 'stop' | 'delete' | 'none'
  enabled: boolean
  label: string
  reason: string | null
}

export function stopControl(session: LiveSession): StopControl {
  const stop = (enabled: boolean, reason: string | null, label = 'Stop session'): StopControl => ({
    kind: 'stop',
    enabled,
    label,
    reason,
  })
  if ((LIVE_ENDED as readonly string[]).includes(session.status)) {
    return { kind: 'delete', enabled: true, label: 'Delete session', reason: null }
  }
  switch (session.status) {
    case 'stalled':
      return stop(true, null)
    case 'starting':
      return stop(false, STOP_REASONS.starting)
    case 'stopping':
      return stop(false, null, STOPPING_LABEL)
    case 'running': {
      const activity = session.state?.activity
      const ready = activity === 'running' || activity === 'waiting_for_feed'
      return ready ? stop(true, null) : stop(false, STOP_REASONS.notReady)
    }
    default:
      if (typeof session.ended_at === 'string') {
        return { kind: 'delete', enabled: true, label: 'Delete session', reason: null }
      }
      return stop(false, STOP_REASONS.unknownStatus)
  }
}

// ---- How a session ended --------------------------------------------------

export interface EndedCopy {
  headline: string
  sentence: string | null
  showErrorBlock: boolean
  reasonCode: string | null
}

export function endedCopy(session: LiveSession): EndedCopy {
  const reason = session.end_reason
  const key = `${session.status}/${reason ?? ''}`
  const known = ENDED_HEADLINES[key]
  const refused = typeof reason === 'string' && reason.startsWith('refused_') && known !== undefined
  const sentenceFor = (): string | null => {
    if (refused) return null
    const pos = session.end_position
    return typeof pos === 'string' ? (POSITION_SENTENCES[pos] ?? null) : null
  }
  const hasError = typeof session.error === 'string' && session.error !== ''
  if (known === undefined) {
    return {
      headline: endedFallbackHeadline(session.status),
      sentence: sentenceFor(),
      showErrorBlock: hasError,
      reasonCode: typeof reason === 'string' ? reason : null,
    }
  }
  return {
    headline: known,
    sentence: sentenceFor(),
    showErrorBlock: session.status === 'failed' && hasError,
    reasonCode: refused && typeof reason === 'string' ? reason : null,
  }
}

export interface FinalRow {
  key: string
  label: string
  value: string
}

export function finalRows(session: LiveSession): FinalRow[] | null {
  const f = session.final
  if (f === null || f === undefined) return null
  const basis = typeof f.close_basis === 'string' ? (CLOSE_BASIS[f.close_basis] ?? f.close_basis) : DASH
  const count = (n: number | null | undefined): string => (finite(n) ? String(n) : DASH)
  return [
    { key: 'balance', label: 'Balance', value: formatMoney(f.balance) },
    { key: 'equity', label: 'Equity', value: formatMoney(f.equity) },
    { key: 'realized', label: 'Realized P&L', value: formatPnl(f.realized_pnl) },
    { key: 'commission', label: 'Commission', value: formatMoney(f.commission_total) },
    { key: 'fills', label: 'Fills', value: count(f.fills) },
    { key: 'orders', label: 'Orders', value: count(f.orders) },
    { key: 'closed', label: 'Positions closed', value: count(f.positions_closed) },
    { key: 'basis', label: 'Close basis', value: basis },
    { key: 'paper', label: 'Paper P&L', value: formatPnl(paperPnl(session)) },
  ]
}

// ---- Contract name, figures -----------------------------------------------

export function contractName(
  session: LiveSession,
  shellSymbols: FeedSymbol[] | null,
): { text: string; caption: string | null } {
  const raw = session.state?.feed?.raw_symbol
  if (typeof raw === 'string' && raw !== '') return { text: raw, caption: null }
  const hit = shellSymbols?.find((s) => s.instrument === session.instrument)
  const contract = hit?.contract
  if (typeof contract === 'string' && contract !== '') {
    return { text: `Feed contract ${contract}`, caption: 'per the market feed now' }
  }
  return { text: DASH, caption: null }
}

export interface FigureCell {
  key: string
  label: string
  value: string
  tone?: 'pos' | 'neg' | 'warn'
  title?: string
  /** A small second line under the value. */
  sub?: string
}

export interface FigureContext {
  shellFeedState: FeedState | null
  shellSymbols: FeedSymbol[] | null
}

const toneOf = (n: number | null | undefined): 'pos' | 'neg' | undefined =>
  !finite(n) || Math.round(n * 100) === 0 ? undefined : n > 0 ? 'pos' : 'neg'

export function figureCells(session: LiveSession, ctx: FigureContext): FigureCell[] {
  const st = session.state
  const ended = isEnded(session)
  const fin = ended ? session.final : null
  const acct = st?.account
  const counts = st?.counts
  const count = (n: number | null | undefined): string => (finite(n) ? String(n) : DASH)
  const balance = fin ? fin.balance : acct?.balance
  const equity = fin ? fin.equity : acct?.equity
  const realized = fin ? fin.realized_pnl : acct?.realized_pnl
  const commission = fin ? fin.commission_total : acct?.commission_total
  const orders = fin ? fin.orders : counts?.orders
  const fills = fin ? fin.fills : counts?.fills
  const closed = fin ? fin.positions_closed : counts?.positions_closed
  const unrealized = st?.position?.unrealized_pnl
  const paper = paperPnl(session)
  const feedState = st?.feed?.state
  const feedText =
    typeof feedState === 'string' && feedState !== ''
      ? `${feedState}${st?.feed?.entries_blocked === true ? ' · entries blocked' : ''}`
      : DASH
  const lastTrade = st?.last_trade_at
  const contract = contractName(session, ctx.shellSymbols)
  return [
    { key: 'position', label: 'Position', value: positionText(st?.position, session.instrument) },
    {
      key: 'last_price',
      label: 'Last price',
      value: formatPrice(st?.last_price, session.instrument),
      title: typeof lastTrade === 'string' ? `Last trade ${localClock(lastTrade)}` : undefined,
      sub: typeof lastTrade === 'string' ? localClock(lastTrade) : undefined,
    },
    { key: 'unrealized', label: 'Unrealized P&L', value: formatPnl(unrealized), tone: toneOf(unrealized) },
    { key: 'realized', label: 'Realized P&L', value: formatPnl(realized), tone: toneOf(realized) },
    { key: 'equity', label: 'Equity', value: formatMoney(equity) },
    { key: 'balance', label: 'Balance', value: formatMoney(balance) },
    { key: 'commission', label: 'Commission', value: formatMoney(commission) },
    {
      key: 'counts',
      label: 'Orders / fills / signals / closed',
      value: `${count(orders)} / ${count(fills)} / ${count(counts?.signals)} / ${count(closed)}`,
    },
    { key: 'last_bar', label: 'Last bar', value: localClock(st?.last_bar_open_at) },
    { key: 'feed', label: 'Feed', value: feedText, tone: st?.feed?.entries_blocked === true ? 'warn' : undefined },
    { key: 'contract', label: 'Contract', value: contract.text, title: contract.caption ?? undefined, sub: contract.caption ?? undefined },
    { key: 'paper', label: 'Paper P&L', value: formatPnl(paper), tone: toneOf(paper) },
  ]
}

// ---- The counter ----------------------------------------------------------

export interface CounterModel {
  text: string
  others: Array<{ slug: string; count: number }>
  heldByOtherText: string | null
  capFullHeld: boolean
}

export function counterModel(
  env: Pick<LiveSessionsEnvelope, 'active' | 'max'> & { own_active?: number | null },
  sessions: LiveSession[],
  currentSlug: string,
  ownFallback: number,
): CounterModel {
  const own = finite(env.own_active) ? env.own_active : ownFallback
  const held = env.active - own
  const counts = new Map<string, number>()
  for (const s of sessions) {
    if (isActive(s) && s.strategy_slug !== currentSlug) counts.set(s.strategy_slug, (counts.get(s.strategy_slug) ?? 0) + 1)
  }
  return {
    text: `${env.active} of ${env.max} live`,
    others: [...counts.entries()].map(([slug, count]) => ({ slug, count })),
    heldByOtherText: held > 0 ? `${held} held by another user` : null,
    capFullHeld: env.active >= env.max && own === 0,
  }
}

// ---- The equity line ------------------------------------------------------

export function equityPoints(points: LiveEquityPoint[]): Array<[number, number]> {
  const byTime = new Map<number, number>()
  for (const p of points) {
    if (!finite(p.equity)) continue
    const ms = parseMs(p.t)
    if (ms === null) continue
    byTime.set(Math.floor(ms / 1000), p.equity)
  }
  return [...byTime.entries()].sort((a, b) => a[0] - b[0])
}
